import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Heart, Award, Calendar, DollarSign, Quote, Users, Loader2, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WallOfHonorPage = () => {
  const { language } = useLanguage();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(false);

  const t = {
    fr: {
      title: 'Mur d\'honneur',
      subtitle: 'Nous remercions chaleureusement nos généreux donateurs qui ont choisi de partager leur soutien publiquement.',
      heroTitle: 'Nos héros du quotidien',
      heroSubtitle: 'Chaque don fait une différence dans la vie des femmes et des enfants.',
      totalDonors: 'donateurs reconnus',
      totalDonated: 'Total donné',
      donations: 'dons',
      memberSince: 'Membre depuis',
      joinThem: 'Rejoignez-les',
      donateNow: 'Faire un don',
      becomeRecognized: 'Voulez-vous être reconnu?',
      createAccount: 'Créer un compte donateur',
      noDonors: 'Soyez le premier à apparaître sur notre mur d\'honneur!',
      viewProfile: 'Voir le profil',
    },
    en: {
      title: 'Wall of Honor',
      subtitle: 'We warmly thank our generous donors who have chosen to share their support publicly.',
      heroTitle: 'Our everyday heroes',
      heroSubtitle: 'Every donation makes a difference in the lives of women and children.',
      totalDonors: 'recognized donors',
      totalDonated: 'Total donated',
      donations: 'donations',
      memberSince: 'Member since',
      joinThem: 'Join them',
      donateNow: 'Donate now',
      becomeRecognized: 'Want to be recognized?',
      createAccount: 'Create a donor account',
      noDonors: 'Be the first to appear on our wall of honor!',
      viewProfile: 'View profile',
    }
  };

  const text = t[language];

  const loadDonors = useCallback(async () => {
    setError(false);
    try {
      const response = await axios.get(`${API}/donor/wall-of-honor`);
      setDonors(response.data.donors || []);
      setTotalCount(response.data.total_count || 0);
    } catch (err) {
      setError(true);
      setDonors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDonors();
  }, [loadDonors]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long'
    });
  };

  const formatAmount = (amount) => {
    return `${amount?.toFixed(0) || '0'}$`;
  };

  // Tier based on donation count
  const getTier = (donationCount) => {
    if (donationCount >= 50) return { name: language === 'fr' ? 'Diamant' : 'Diamond', color: 'from-purple-400 to-purple-600', icon: '💎' };
    if (donationCount >= 25) return { name: language === 'fr' ? 'Or' : 'Gold', color: 'from-yellow-400 to-yellow-600', icon: '🥇' };
    if (donationCount >= 10) return { name: language === 'fr' ? 'Argent' : 'Silver', color: 'from-gray-300 to-gray-500', icon: '🥈' };
    if (donationCount >= 5) return { name: language === 'fr' ? 'Bronze' : 'Bronze', color: 'from-amber-600 to-amber-800', icon: '🥉' };
    return { name: language === 'fr' ? 'Ami' : 'Friend', color: 'from-[#4A708B] to-[#3B5A6F]', icon: '💙' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A708B]" />
      </div>
    );
  }

  return (
    <div data-testid="wall-of-honor-page" className="min-h-screen bg-gradient-to-b from-[#D4AF37]/10 to-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] mb-6">
            <Award className="w-5 h-5" />
            <span className="font-medium">{text.title}</span>
          </div>
          
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mb-6">
            {text.heroTitle}
          </h1>
          
          <p className="text-lg text-[#546A76] max-w-2xl mx-auto mb-8">
            {text.subtitle}
          </p>

          {totalCount > 0 && (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white shadow-sm">
              <Users className="w-5 h-5 text-[#4A708B]" />
              <span className="font-semibold text-[#1A252C]">{totalCount}</span>
              <span className="text-[#546A76]">{text.totalDonors}</span>
            </div>
          )}
        </div>
      </section>

      {/* Donors Grid */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {error ? (
            <div className="text-center py-20 bg-white rounded-3xl shadow-sm">
              <Heart className="w-16 h-16 text-red-200 mx-auto mb-6" />
              <p className="text-[#546A76] text-lg mb-6">
                {language === 'fr' 
                  ? 'Une erreur est survenue. Veuillez réessayer.'
                  : 'An error occurred. Please try again.'}
              </p>
              <Button onClick={loadDonors} className="primary-btn rounded-full px-8">
                {language === 'fr' ? 'Réessayer' : 'Retry'}
              </Button>
            </div>
          ) : donors.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {donors.map((donor, index) => {
                const tier = getTier(donor.donation_count);
                return (
                  <div
                    key={donor.id}
                    data-testid={`donor-card-${index}`}
                    className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    {/* Tier Header */}
                    <div className={`bg-gradient-to-r ${tier.color} px-6 py-3 flex items-center justify-between`}>
                      <span className="text-white font-medium flex items-center gap-2">
                        <span>{tier.icon}</span>
                        {tier.name}
                      </span>
                      <span className="text-white/80 text-sm">
                        {donor.donation_count} {text.donations}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center text-white text-xl font-bold">
                          {donor.display_name?.charAt(0)?.toUpperCase() || 'D'}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#1A252C] text-lg">
                            {donor.display_name}
                          </h3>
                          <p className="text-sm text-[#546A76] flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {text.memberSince} {formatDate(donor.member_since)}
                          </p>
                        </div>
                      </div>

                      {/* Amount (if shown) */}
                      {donor.total_donated !== undefined && (
                        <div className="flex items-center gap-2 mb-4 px-4 py-2 rounded-xl bg-[#D4AF37]/10">
                          <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                          <span className="font-bold text-[#D4AF37]">
                            {formatAmount(donor.total_donated)} CAD
                          </span>
                          <span className="text-sm text-[#546A76]">{text.totalDonated.toLowerCase()}</span>
                        </div>
                      )}

                      {/* Message */}
                      {donor.public_message && (
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <Quote className="w-4 h-4 text-[#4A708B] mb-2" />
                          <p className="text-sm text-[#546A76] italic">
                            "{donor.public_message}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-3xl shadow-sm">
              <Heart className="w-16 h-16 text-[#E2E8F0] mx-auto mb-6" />
              <p className="text-[#546A76] text-lg mb-6">{text.noDonors}</p>
              <Link to="/donate">
                <Button className="gold-btn rounded-full px-8">
                  {text.donateNow}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#4A708B] to-[#3B5A6F] rounded-3xl p-8 sm:p-12 text-center text-white">
            <Star className="w-12 h-12 mx-auto mb-6 text-[#D4AF37]" />
            <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-4">
              {text.becomeRecognized}
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              {text.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/donate">
                <Button className="gold-btn rounded-full px-8 py-6 text-lg">
                  <Heart className="w-5 h-5 mr-2" />
                  {text.donateNow}
                </Button>
              </Link>
              <Link to="/donor/register">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg bg-white/10 border-white/30 text-white hover:bg-white/20">
                  {text.createAccount}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WallOfHonorPage;
